'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    return /relation.*does not exist|column.*does not exist|function.*does not exist|syntax error/i.test(error.message);
  }
  return false;
}

export async function getProfileByUserId(userId: string): Promise<any | null> {
  const supabase = createClient();

  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      if (isSchemaError(error)) throw error;
      console.error('getProfileByUserId error:', error.message);
      return null;
    }

    return data ? {
      id: data.id,
      username: data.username || data.email?.split('@')[0],
      displayName: data.full_name,
      joinedAt: new Date(data.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
      location: data.location || '',
      bio: data.bio || '',
      role: data.role,
      reputationScore: data.reputation_score,
      tier: data.tier,
      contributionCount: data.contribution_count,
      publishedCount: data.published_count,
      corroborationsReceived: data.corroborations_received,
      corroborationsGiven: data.corroborations_given,
      specialisations: data.specialisations || [],
    } : null;
  } catch (error: any) {
    console.error('getProfileByUserId error:', error.message);
    throw error;
  }
}

export async function getLeaderboard(): Promise<any[]> {
  const supabase = createClient();

  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, full_name, username, tier, reputation_score, published_count, corroborations_received, specialisations, created_at, updated_at')
      .eq('is_active', true)
      .order('reputation_score', { ascending: false })
      .limit(50);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('getLeaderboard error:', error.message);
      return [];
    }

    const rows = data || [];

    // Fetch disputed contribution counts per user
    const userIds = rows.map((r: any) => r.id);
    let disputedMap: Record<string, number> = {};
    if (userIds.length > 0) {
      const { data: disputedData } = await supabase
        .from('contributions')
        .select('author_id')
        .in('author_id', userIds)
        .eq('status', 'disputed');
      for (const d of disputedData || []) {
        disputedMap[d.author_id] = (disputedMap[d.author_id] || 0) + 1;
      }
    }

    return rows.map((row: any, idx: number) => ({
      id: row.id,
      rank: idx + 1,
      username: row.username || row.full_name?.toLowerCase().replace(' ', '.'),
      displayName: row.full_name,
      tier: row.tier,
      reputationScore: row.reputation_score,
      publishedCount: row.published_count,
      corroborationsReceived: row.corroborations_received,
      disputedCount: disputedMap[row.id] || 0,
      specialisations: row.specialisations || [],
      joinedAt: new Date(row.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
      lastActive: new Date(row.updated_at).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      }),
      eraFocus: row.specialisations?.[0] || '',
    }));
  } catch (error: any) {
    console.error('getLeaderboard error:', error.message);
    throw error;
  }
}

export async function updateProfile(userId: string, updates: {
  bio?: string;
  location?: string;
  specialisations?: string[];
}): Promise<boolean> {
  const supabase = createClient();

  try {
    const { error } = await supabase
      .from('user_profiles')
      .update({
        bio: updates.bio,
        location: updates.location,
        specialisations: updates.specialisations,
      })
      .eq('id', userId);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('updateProfile error:', error.message);
      return false;
    }
    return true;
  } catch (error: any) {
    console.error('updateProfile error:', error.message);
    throw error;
  }
}

export async function getActivityByUserId(userId: string, limit = 20): Promise<any[]> {
  const supabase = createClient();

  try {
    const { data, error } = await supabase
      .from('activity_feed')
      .select('id, event_type, ref_id, ref_title, ref_url, created_at, metadata')
      .eq('actor_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('getActivityByUserId error:', error.message);
      return [];
    }

    return (data || []).map((row: any) => {
      const typeMap: Record<string, string> = {
        new_contribution: 'submitted',
        corroboration: 'corroborated',
        comment: 'commented',
        forum_post: 'forum_post',
        ama_answer: 'ama_answer',
        gap_challenge: 'gap_challenge',
      };
      return {
        id: row.id,
        type: typeMap[row.event_type] || row.event_type,
        description: row.ref_title
          ? `${row.event_type === 'new_contribution' ? 'Submitted' : row.event_type === 'corroboration' ? 'Corroborated' : 'Activity on'} "${row.ref_title}"`
          : row.metadata?.description || row.event_type,
        date: new Date(row.created_at).toLocaleDateString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric',
        }),
        contributionId: row.ref_id || undefined,
      };
    });
  } catch (error: any) {
    console.error('getActivityByUserId error:', error.message);
    return [];
  }
}
